import {Component, OnDestroy, OnInit, ViewChild} from '@angular/core';
import {DomSanitizer, SafeResourceUrl} from '@angular/platform-browser';
import {ActivatedRoute, Router} from '@angular/router';
import {Subject} from 'rxjs';
import {takeUntil} from 'rxjs/operators';
import {ConstantsService} from '../../fidelidade/ConstantsService';
import {EstadoOnboardingPainel, OnboardingPainelService, PASSOS_ONBOARDING, PassoOnboarding, PassoOnboardingId}
  from '../onboarding-painel.service';
import {OnbAjudaComponent} from '../apoio/onb-ajuda.component';
import {OnbFocoService} from '../apoio/onb-foco.service';

/**
 * Ícone de cada passo (o colorido serve para reconhecer o passo antes de ler) e o peso dele na estimativa do total.
 * Os minutos são chute, não medição: por isso não aparecem por passo, só somados e com folga no cabeçalho.
 */
const VISUAL: Record<PassoOnboardingId, {icone: string; minutos: number}> = {
  cardapio: {icone: 'fas fa-book-open', minutos: 3},
  loja: {icone: 'fas fa-store', minutos: 2},
  pagamento: {icone: 'far fa-credit-card', minutos: 1},
  receber: {icone: 'fas fa-bell', minutos: 1},
  teste: {icone: 'fas fa-shopping-bag', minutos: 2}
};

/**
 * Primeiros passos (/admin/comecar e /admin/comecar/:passo): o hub com os cinco passos da loja criada pela Mia Gestor
 * e, dentro dele, cada passo compacto. Ao salvar um passo, segue para o próximo que falta; no fim, a tela Pronto.
 */
@Component({
  selector: 'app-comecar',
  templateUrl: './comecar.component.html',
  styleUrls: ['./comecar.component.scss']
})
export class ComecarComponent implements OnInit, OnDestroy {
  @ViewChild(OnbAjudaComponent) ajudaPainel: OnbAjudaComponent;

  estado: EstadoOnboardingPainel | null = null;
  empresa: any = {};
  passo: string | null = null;
  carregando = true;
  erro = '';
  urlPrevia: SafeResourceUrl | null = null;
  private linkPrevia = '';
  copiado = false;
  erroCopia = '';

  private destruir$ = new Subject<void>();

  constructor(private onboarding: OnboardingPainelService, private constants: ConstantsService, private router: Router,
              private route: ActivatedRoute, private foco: OnbFocoService, private sanitizer: DomSanitizer) {}

  ngOnInit(): void {
    this.constants.empresa$.pipe(takeUntil(this.destruir$)).subscribe(empresa => {
      this.empresa = empresa || {};
      const link = this.empresa.linkLoja || '';
      if (link === this.linkPrevia) return;
      this.linkPrevia = link;
      this.urlPrevia = null;
      if (!link) return;
      try {
        const url = new URL(link, window.location.origin);
        if (url.protocol === 'https:' || url.protocol === 'http:') {
          this.urlPrevia = this.sanitizer.bypassSecurityTrustResourceUrl(url.href);
        }
      } catch (_) {
        this.urlPrevia = null;
      }
    });
    this.route.paramMap.pipe(takeUntil(this.destruir$)).subscribe(params => {
      const passo = params.get('passo');
      this.passo = passo && ([...PASSOS_ONBOARDING, 'pronto'] as string[]).includes(passo) ? passo : null;
      this.foco.feche();
      window.scrollTo(0, 0);
      if (this.passo && this.passo !== 'pronto') this.onboarding.registre('PASSO_ABERTO', this.passo);
      this.carregue();
    });
  }

  ngOnDestroy(): void {
    this.foco.feche();
    this.destruir$.next();
    this.destruir$.complete();
  }

  carregue(): void {
    this.carregando = !this.estado;
    this.erro = '';
    this.onboarding.estado().then(estado => this.estado = estado)
      .catch(() => this.erro = 'Não foi possível carregar os primeiros passos.')
      .finally(() => this.carregando = false);
  }

  get percentual(): number { return this.estado?.total ? Math.round((this.estado.feitos || 0) / this.estado.total * 100) : 0; }

  async copieLink(): Promise<void> {
    this.erroCopia = '';
    this.copiado = false;
    try {
      await navigator.clipboard.writeText(this.empresa.linkLoja);
      this.copiado = true;
    } catch (_) {
      this.erroCopia = 'Não foi possível copiar. Selecione o endereço acima para copiar o link.';
    }
  }

  get faltam(): number { return (this.estado?.total || 0) - (this.estado?.feitos || 0); }

  get minutosQueFaltam(): number { return (this.estado?.passos || []).filter(p => !p.feito).reduce((total, p) => total + VISUAL[p.id].minutos, 0); }

  /**
   * O total com folga (50% a mais, de 5 em 5 minutos): o passo que mais pesa, o cardápio, varia muito com a
   * importação, e estimativa curta demais parece travada quando estoura.
   */
  get textoTempo(): string {
    const minutos = Math.max(5, Math.ceil(this.minutosQueFaltam * 1.5 / 5) * 5);
    return `~${minutos} minutos`;
  }

  /** com o cardápio vazio a loja abre sem nada para vender: o texto não diz "no ar" */
  get cardapioPronto(): boolean { return !!this.estado?.passos?.find(p => p.id === 'cardapio')?.feito; }

  /** produtos já chegaram, mas nenhum com preço: a loja segue vazia */
  get cardapioIniciado(): boolean { return !!this.estado?.passos?.find(p => p.id === 'cardapio')?.iniciado; }

  /** A prévia vazia abre o passo Cardápio já no caminho escolhido (o passo lê o ?caminho=). */
  abraCardapio(caminho?: 'foto' | 'texto' | 'planilha' | 'ifood' | 'sistema' | 'manual'): void {
    this.router.navigate(['/admin/comecar', 'cardapio'], caminho ? {queryParams: {caminho}} : {});
  }

  get linkCurto(): string { return String(this.empresa?.linkLoja || '').replace(/^https?:\/\//, '').replace(/\/$/, ''); }

  icone(p: PassoOnboarding): string { return p.feito ? 'fas fa-check' : VISUAL[p.id].icone; }

  ehProximo(p: PassoOnboarding): boolean { return !p.feito && this.estado?.proximo === p.id; }

  abra(p: PassoOnboarding | PassoOnboardingId): void {
    const id = typeof p === 'string' ? p : p.id;
    this.router.navigate(['/admin/comecar', id]);
  }

  /** Passo salvo: atualiza o estado e segue para o próximo que falta (ou volta ao hub). */
  aoConcluirPasso(estado?: EstadoOnboardingPainel): void {
    const anterior = this.passo;
    const seguir = (novo: EstadoOnboardingPainel) => {
      this.estado = novo;
      if (anterior) this.onboarding.registre('PASSO_FEITO', anterior);
      if (anterior === 'receber') this.constants.recarregueEmpresa();
      const proximo = novo?.proximo;
      if (proximo && proximo !== anterior) this.router.navigate(['/admin/comecar', proximo]);
      else this.router.navigate(['/admin/comecar']);
    };
    if (estado) seguir(estado);
    else this.onboarding.estado().then(seguir).catch(() => this.router.navigate(['/admin/comecar']));
  }

  aoConcluirTeste(estado: EstadoOnboardingPainel): void {
    this.estado = estado;
    this.onboarding.registre('PASSO_FEITO', 'teste');
    // o menu passa para a fase Pronta e o Início deixa de redirecionar para cá
    this.constants.recarregueEmpresa();
    this.router.navigate(['/admin/comecar', 'pronto']);
  }

  atualize(estado: EstadoOnboardingPainel): void { this.estado = estado; }

  pecaAjudaDe(evento: {motivo: string; passo: string}): void { this.ajudaPainel?.abra(evento.motivo, evento.passo); }
}
